import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"

// smart_time_estimate_controller
//
// Non-blocking "Smart Suggestion" widget for the task and task-template
// forms. Whenever the task type changes, fetches a suggested duration from
// SmartTimeEstimateService — a plain history/statistics lookup, no AI/LLM
// call — and renders "Suggested: Xh based on N similar completed tasks."
//
// This is intentionally labeled "Smart Suggestion", not "AI", because no
// model inference is involved.
//
// Usage:
//   <div data-controller="smart-time-estimate"
//        data-smart-time-estimate-url-value="<smart_time_estimate_company_tasks_path>"
//        data-smart-time-estimate-fallback-hours-value="<template estimated_hours, optional>">
//     <select data-smart-time-estimate-target="taskTypeSelect"
//             data-action="change->smart-time-estimate#refresh">...</select>
//     <input data-smart-time-estimate-target="estimatedHoursInput" type="number" ...>
//     <div data-smart-time-estimate-target="suggestion"></div>
//   </div>

export default class extends Controller {
  static targets = ["taskTypeSelect", "estimatedHoursInput", "suggestion"]
  static values = { url: String, fallbackHours: Number }

  connect() {
    this.refresh()
  }

  refresh() {
    const taskType = this.hasTaskTypeSelectTarget ? this.taskTypeSelectTarget.value : null
    if (!this.hasUrlValue || !taskType) {
      this.#render(null)
      return
    }

    const params = new URLSearchParams({ task_type: taskType })
    if (this.hasFallbackHoursValue && this.fallbackHoursValue > 0) {
      params.set("fallback_hours", this.fallbackHoursValue)
    }

    // Debounce so rapid selection changes don't fire overlapping requests.
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.#fetchSuggestion(params), 150)
  }

  apply(event) {
    const hours = event.currentTarget?.dataset.hours
    if (!hours || !this.hasEstimatedHoursInputTarget) return
    this.estimatedHoursInputTarget.value = hours
    this.estimatedHoursInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // -- private helpers -------------------------------------------------------

  async #fetchSuggestion(params) {
    const requestToken = (this.requestToken = Symbol())

    try {
      const response = await fetch(`${this.urlValue}?${params.toString()}`, {
        headers: { "Accept": "application/json" }
      })

      // Ignore stale responses if the task type changed again mid-flight.
      if (this.requestToken !== requestToken) return

      if (!response.ok) {
        this.#render(null)
        return
      }

      this.#render(await response.json())
    } catch (e) {
      if (this.requestToken !== requestToken) return
      this.#render(null)
    }
  }

  #render(data) {
    if (!this.hasSuggestionTarget) return

    if (!data || !data.suggested_hours) {
      this.suggestionTarget.innerHTML = ""
      return
    }

    const hours = Number(data.suggested_hours)
    const formatted = this.#formatHours(hours)

    let detail
    if (data.source === "history") {
      detail = t("javascript.controllers.smart_time_estimate_controller.suggested_from_history", { hours: formatted, count: data.sample_size })
    } else if (data.source === "fallback") {
      detail = t("javascript.controllers.smart_time_estimate_controller.suggested_from_policy", { hours: formatted })
    } else {
      this.suggestionTarget.innerHTML = ""
      return
    }

    const showApply = data.source === "history" && this.hasEstimatedHoursInputTarget

    this.suggestionTarget.innerHTML = `
      <div class="flex items-center gap-2 mt-2 text-sm">
        <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200 text-xs font-medium shrink-0">
          <span class="material-symbols-outlined !text-sm">lightbulb</span>
          ${t("javascript.controllers.smart_time_estimate_controller.smart_suggestion")}
        </span>
        <span class="text-gray-600">${this.#escape(detail)}</span>
        ${showApply
          ? `<button type="button" data-action="smart-time-estimate#apply" data-hours="${hours}"
                     class="text-xs font-medium text-primary hover:underline shrink-0">${t("javascript.controllers.smart_time_estimate_controller.use_this")}</button>`
          : ""}
      </div>
    `
  }

  #formatHours(hours) {
    if (!Number.isFinite(hours)) return "0"
    return (Math.round(hours * 100) / 100).toString()
  }

  #escape(str) {
    return String(str)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;")
      .replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
